import { ENEMIES } from '../../data/enemies';
import { SIM_HZ } from '../../data/tuning';
import { createAabb, type Aabb } from '../collision';

/**
 * EN-42 Dron fronterizo (GDD §8.3, issue #128). A Border Patrol drone that patrols vertically between two heights
 * at 3 u/s, with an exact rebound (no pause at the ends), turning an open stretch into a timing problem. It does
 * not move in x with respect to the world: it drifts with the scroll, like a stationary EN-02. It is part of the
 * law, so it is a hazard that is dodged and never destroyed (GDD §3.4, §4.4). Pure and deterministic: the height
 * is a function of the ticks since it was active, computed from the parameters, never accumulated.
 */

export const EN42 = ENEMIES['EN-42'];

/** Vertical speed in u per tick. */
export const DRONE_UP = EN42.patrolSpeed / SIM_HZ;

export interface BorderDroneState {
  /** Ticks since the drone was first active; the position is a function of it. */
  ticks: number;
}

export const createBorderDrone = (): BorderDroneState => ({ ticks: 0 });

export interface DroneParams {
  yMin: number;
  yMax: number;
  /** Initial direction: 1 up, -1 down. */
  dir: number;
}

/**
 * Height of the drone `ticks` ticks after it was active, for a patrol between `yMin` and `yMax` starting at `startY`
 * moving `dir`. The rebound is exact: the excess past a limit is reflected, so the extremes are hit at the exact
 * tick (e.g. from y 4, up, at 0.05 u/tick it reaches y 8 at tick 80 and is back at 6 at tick 120).
 */
export function droneY(startY: number, ticks: number, params: DroneParams): number {
  const span = params.yMax - params.yMin;
  if (span <= 0) return startY;
  // Position along a reflecting segment [0, 2*span): 0 at yMin, span at yMax, back to 0.
  const travelled = (startY - params.yMin) + params.dir * DRONE_UP * ticks;
  const period = 2 * span;
  let phase = travelled % period;
  if (phase < 0) phase += period;
  return phase <= span ? params.yMin + phase : params.yMin + (period - phase);
}

/** The drone body: an AABB of `hitboxWidth` x `hitboxHeight` u (70-80 % of the model, GDD §8.1). */
export const droneBody = (x: number, y: number): Aabb => createAabb(x, y, EN42.hitboxWidth, EN42.hitboxHeight);

/**
 * Whether a patrol is valid: strictly inside the playable band (GDD §5.2), with `yMin < yMax`, and half the hitbox
 * within the band (the drone never overlaps the ground or the ceiling).
 */
export function validateDroneParams(params: Partial<DroneParams>, groundY: number, worldTop: number): string | null {
  const { yMin, yMax } = params;
  if (yMin === undefined || yMax === undefined) return 'en42-missing-bounds: yMin and yMax are required';
  if (!(yMin < yMax)) return 'yMin must be below yMax';
  const half = EN42.hitboxHeight / 2;
  if (yMin - half <= groundY || yMax + half >= worldTop) return 'patrol outside playfield';
  return null;
}
